import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Copy,
  GitMerge,
  Layers,
  Lightbulb,
  ShieldAlert,
  Sparkles,
  Wand2,
  Zap,
} from "lucide-react";
import LIVE_FUSION_EXAMPLE from "../data/liveFusionExample";
import { openEmergentWithPrompt } from "../lib/emergentLink";

function copyText(text) {
  navigator.clipboard?.writeText(text || "").catch(() => {});
}

function InputPill({ p }) {
  return (
    <div className="border border-[#27272A] bg-[#0A0A0A] p-3 flex flex-col gap-1">
      <div className="text-[9.5px] font-mono uppercase tracking-[0.2em] text-zinc-500">
        {p.category}
      </div>
      <div className="text-[12.5px] text-white leading-snug">{p.title}</div>
      <div className="text-[11px] text-zinc-400 leading-snug">
        {p.one_liner}
      </div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-auto">
        user · {p.target_user}
      </div>
    </div>
  );
}

function BuildPromptCardLanding({ idx, text }) {
  const [copied, setCopied] = useState(false);
  const label = idx === 0 ? "MVP_SCAFFOLD" : "DESIGN_UX";
  const copy = () => {
    copyText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  const openEmergent = async () => {
    await openEmergentWithPrompt(text);
  };
  return (
    <div className="border border-[#27272A] bg-[#0E0E0E] flex flex-col">
      <div className="px-3 py-2 border-b border-[#27272A] flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-mono uppercase tracking-wider text-amber-300">
          PROMPT_{idx + 1} · {label}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button
            data-testid={`live-fusion-prompt-copy-${idx}`}
            onClick={copy}
            className="inline-flex items-center gap-1 h-6 px-2 text-[9.5px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:bg-[#141414] hover:text-white"
          >
            <Copy size={10} /> {copied ? "COPIED" : "COPY"}
          </button>
          <button
            data-testid={`live-fusion-prompt-open-emergent-${idx}`}
            onClick={openEmergent}
            className="inline-flex items-center gap-1 h-6 px-2 text-[9.5px] font-mono uppercase tracking-wider border border-amber-400 bg-amber-400/10 text-amber-200 hover:bg-amber-400 hover:text-black"
          >
            <Zap size={10} /> BUILD_IN_EMERGENT
          </button>
        </div>
      </div>
      <pre className="p-3 text-[11.5px] text-zinc-100 whitespace-pre-wrap font-mono leading-relaxed max-h-72 overflow-auto">
        {text}
      </pre>
    </div>
  );
}

function Bullets({ items, dot = "amber", testId }) {
  const dotColor = {
    amber: "bg-amber-400",
    emerald: "bg-emerald-400",
    purple: "bg-purple-400",
  }[dot];
  return (
    <ul data-testid={testId} className="space-y-1.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2 text-[12.5px] text-zinc-300 leading-snug">
          <span className={`shrink-0 mt-2 w-1.5 h-1.5 ${dotColor}`} />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

export default function LiveFusionExample() {
  const ex = LIVE_FUSION_EXAMPLE;
  const fc = ex.fused_concept;

  return (
    <section
      data-testid="landing-live-fusion-example"
      className="max-w-7xl mx-auto px-4 sm:px-6 py-12"
    >
      {/* header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em]">
            <span className="text-emerald-400">LIVE_EXAMPLE</span>
            <span className="text-zinc-600">·</span>
            <span className="text-purple-300">PLATFORM_CROSS-FUSION</span>
            <span className="border border-emerald-400/40 bg-emerald-500/[0.06] text-emerald-300 px-1.5 py-0.5 text-[9px] tracking-wider">
              REAL_OUTPUT · NOT A MOCKUP
            </span>
          </div>
          <h2 className="mt-2 text-2xl sm:text-3xl font-mono tracking-tight text-white max-w-3xl leading-tight">
            Two contrasting platforms in.{" "}
            <span className="text-amber-300">One fused concept — plus two build-ready prompts — out.</span>
          </h2>
          <p className="mt-2 text-sm text-zinc-400 leading-relaxed max-w-3xl">
            The output below was generated by MindStein&apos;s Cross-Fusion mode
            from the two platform ideas on the left. Nothing on this page is
            a mockup — the same structure appears every time you fuse ideas
            inside the Innovation section.
          </p>
        </div>
        <Link
          to="/innovation"
          data-testid="live-fusion-cta"
          className="inline-flex items-center gap-2 h-9 px-3 border border-amber-400 bg-amber-400 text-black text-[11px] font-mono uppercase tracking-wider hover:bg-amber-300"
        >
          <GitMerge size={12} /> TRY_CROSS-FUSION
          <ArrowRight size={12} />
        </Link>
      </div>

      {/* input strip */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-3">
        <InputPill p={ex.input_platforms[0]} />
        <div className="flex flex-col items-center gap-1 py-2">
          <GitMerge size={22} className="text-purple-300" />
          <span className="text-[9.5px] font-mono uppercase tracking-[0.25em] text-purple-300">
            FUSE
          </span>
        </div>
        <InputPill p={ex.input_platforms[1]} />
      </div>

      {/* Section 1 · COMMON PATTERNS */}
      <div className="mt-6 border border-[#27272A] bg-[#0A0A0A] p-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-300 mb-3 flex items-center gap-2">
          <Layers size={12} /> 1 · COMMON_PATTERNS
          <span className="text-zinc-500 normal-case tracking-normal text-[11px]">
            · structural signals shared across the selected platforms
          </span>
        </div>
        <Bullets
          items={ex.common_patterns}
          testId="live-fusion-patterns"
          dot="amber"
        />
      </div>

      {/* Section 2 · FUSION OPPORTUNITY */}
      <div className="mt-3 border-2 border-purple-400/50 bg-purple-500/[0.06] p-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-purple-200 flex items-center gap-2">
          <Sparkles size={12} /> 2 · FUSION_OPPORTUNITY
          <span className="text-zinc-400 normal-case tracking-normal text-[11px]">
            · the combined use case
          </span>
        </div>
        <h3 className="mt-2 text-[19px] leading-tight text-white">
          {fc.title}
        </h3>
        <p className="mt-1 text-[13.5px] text-zinc-200 leading-relaxed max-w-4xl">
          {fc.one_liner}
        </p>
        <div className="mt-2 text-[10.5px] font-mono uppercase tracking-wider text-purple-200">
          target_user · {fc.target_user}
        </div>
        <p className="mt-3 text-[13px] text-zinc-100 leading-relaxed border-l-4 border-purple-400 pl-3 py-1 max-w-4xl">
          {fc.combined_use_case}
        </p>
        <div className="mt-3 text-[12px] text-purple-100 leading-snug max-w-4xl">
          <span className="text-purple-300 font-mono uppercase text-[9.5px] tracking-[0.2em] mr-2">
            differentiator ·
          </span>
          {fc.differentiator}
        </div>
      </div>

      {/* Section 3 · DESIGN SUGGESTION */}
      <div className="mt-3 border border-[#27272A] bg-[#0A0A0A] p-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-300 mb-3 flex items-center gap-2">
          <Lightbulb size={12} /> 3 · DESIGN_SUGGESTION
          <span className="text-zinc-500 normal-case tracking-normal text-[11px]">
            · how the fused platform could work
          </span>
        </div>
        <Bullets
          items={fc.design_suggestions}
          testId="live-fusion-design"
          dot="emerald"
        />
      </div>

      {/* Section 4 · BUILD PROMPTS + Emergent CTA */}
      <div className="mt-3 border border-amber-400/30 bg-[#0A0A0A] p-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-300 mb-3 flex items-center gap-2">
          <Wand2 size={12} /> 4 · READY-TO-USE_BUILD_PROMPTS
          <span className="text-zinc-500 normal-case tracking-normal text-[11px]">
            · copy either one into your builder to start
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ex.build_prompts.map((p, i) => (
            <BuildPromptCardLanding key={i} idx={i} text={p} />
          ))}
        </div>
        <div
          data-testid="live-fusion-emergent-cta"
          className="mt-4 pt-3 border-t border-[#161616] text-[12px] font-mono text-zinc-300"
        >
          Best built with{" "}
          <a
            data-testid="live-fusion-emergent-link"
            href="https://emergent.sh"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-300 underline underline-offset-2 hover:text-amber-200"
          >
            Emergent
          </a>{" "}
          — the AI platform builder most compatible with this output.
        </div>
      </div>

      {/* Patent block */}
      <div className="mt-3 border border-amber-400/30 bg-amber-500/[0.04] p-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-300 mb-2 flex items-center gap-1">
          <ShieldAlert size={11} /> CHECK_FOR_PATENT_CONFLICTS · DIY
        </div>
        <div className="flex flex-wrap gap-2">
          {ex.patent_search_links.map((l) => (
            <a
              key={l.name}
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 h-7 px-2 text-[10.5px] font-mono uppercase tracking-wider border border-[#27272A] bg-[#0A0A0A] text-zinc-300 hover:border-amber-400/50 hover:text-amber-200"
            >
              {l.name}
            </a>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-zinc-400 leading-snug">
          {ex.patent_disclaimer}
        </p>
      </div>
    </section>
  );
}
